import { spItemKeyFacet, spPageContextKeyFacet, spListKeyFacet, spItemFullUrlKeyFacet } from '../dataSource/Keys';
import { spPageContextFacet, folderPathFacet, listFacet, listItemIdFacet, isRootFolderFacet, uniqueIdFacet } from '../dataSource/Facets';
import { getAuthToken } from '../dataSource/getData';
import { IControlHandler, createControlHandler } from '@ms/items-view/lib/private/addons/registerControlHandler';
import { shareActionControl, ISharingInformation } from '@ms/items-view/lib/private/controls/shareActionControl';
import { IItemCacheThunkAction, IItemCacheDispatch } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { IItemCache } from '@ms/items-view/lib/private/models/IItemCacheObserver';
import { getSharingClientId } from '@ms/items-view/lib/private/operations/getSharingClientId';
import { folderFacet } from '@ms/items-view/lib/private/facets/ItemFacets';

export const shareActionControlHandler: IControlHandler<typeof spItemKeyFacet.type, typeof shareActionControl.type> =
  createControlHandler()(getShareActionControl);
export const itemFullUrlshareActionControlHandler: IControlHandler<typeof spItemFullUrlKeyFacet.type, typeof shareActionControl.type> =
  createControlHandler()(getShareActionControl);

function getShareActionControl(): IItemCacheThunkAction<typeof shareActionControl.type> {
  return (dispatch: IItemCacheDispatch) => {
    return (itemCache: IItemCache, props: { itemKey: string; }): {
      isAvailable: boolean;
      getSharingInformation?: () => IItemCacheThunkAction<Promise<ISharingInformation>>;
    } => {
      const {
        itemKey
      } = props;

      const key = itemCache.demandItemFacet(spItemKeyFacet, itemKey);

      let isAvailable = false;
      let getSharingInformation: (() => IItemCacheThunkAction<Promise<ISharingInformation>>) | undefined = undefined;

      if (key) {
        const {
          listFullUrl,
          webAbsoluteUrl
        } = key;

        // Anonymous guest user cannot share
        const pageContext = key && itemCache.demandItemFacet(
          spPageContextFacet,
          spPageContextKeyFacet.serialize({
            webAbsoluteUrl: key.webAbsoluteUrl,
            listFullUrl: key.listFullUrl
          }));

        isAvailable = !!(pageContext && !pageContext.isAnonymousGuestUser);

        // Build callback to retrieve sharing information
        if (isAvailable) {
          // sharing action is not available in rootfolder.
          const isRootFolder = itemCache.demandItemFacet(isRootFolderFacet, itemKey);

          isAvailable = !isRootFolder;

          if (isAvailable) {
            getSharingInformation = () => {
              return _getSharingInformation({
                itemKey,
                listFullUrl,
                webAbsoluteUrl
              });
            };
          }
        }
      }

      return {
        isAvailable: !!(isAvailable && getSharingInformation),
        getSharingInformation: getSharingInformation
      };
    };
  };
}

function _getSharingInformation(payload: {
  itemKey: string | undefined;
  listFullUrl: string;
  webAbsoluteUrl: string;
}): IItemCacheThunkAction<Promise<ISharingInformation>> {
  const {
    itemKey,
    webAbsoluteUrl,
    listFullUrl
  } = payload;

  return async (dispatch: IItemCacheDispatch) => {
    const authToken = await dispatch(getAuthToken({
      url: webAbsoluteUrl || listFullUrl
    }));
    const {
      demandItems
    } = await import('@ms/items-view/lib/private/operations/DeferredOperations');

    // Send different information depending on whether we're
    // using token based authentication or cookie based
    if (authToken) {
      const {
        folderPath,
        isFolder,
        uniqueId
      } = await dispatch(demandItems((itemCache: IItemCache) => {
        return {
          folderPath: itemCache.demandItemFacet(folderPathFacet, itemKey),
          isFolder: !!itemCache.demandItemFacet(folderFacet, itemKey),
          uniqueId: itemCache.demandItemFacet(uniqueIdFacet, itemKey)
        };
      }));

      return {
        authToken: authToken,
        webAbsoluteUrl: webAbsoluteUrl,
        listUrl: listFullUrl,
        serverRelativeItemUrl: folderPath,
        isFolder: isFolder,
        uniqueId: uniqueId
      };
    } else {
      const {
        listId,
        listItemId
      } = await dispatch(demandItems((itemCache: IItemCache) => {
        const listKey = spListKeyFacet.serialize({
          webAbsoluteUrl,
          listFullUrl
        });

        const listInfo = itemCache.demandItemFacet(listFacet, listKey);

        return {
          listItemId: itemCache.demandItemFacet(listItemIdFacet, itemKey),
          listId: (listInfo && listInfo.id) || ''
        };
      }));

      const sharingClientId: string = dispatch(getSharingClientId());
      const cookieAuthIframeUrl = listId && listItemId ?
        /* tslint:disable-next-line:max-line-length */
        `${webAbsoluteUrl}/_layouts/15/sharedialog.aspx?listId=${listId}&listItemId=${listItemId}&clientId=${sharingClientId}` :
        undefined;
      return {
        cookieAuthIframeUrl: cookieAuthIframeUrl
      };
    }
  };
}
